import React, { useEffect, useState } from 'react';

/**
 * Segmented score slider with a semantic single-color bar (red→green).
 * Default range is 1-5; pass `min={0}` for the 0-5 (six-step) control scale
 * where 0 = "does not exist / no effectiveness". The numeric model is unchanged
 * — only the selectable range widens.
 * Used in:
 *  - Internal Controls test runner (TestProgramExecutionModal) — min=0
 *  - Ad-hoc control scoring (ScoreControlModal) — min=0
 *  - Risk Assessment scoring (ScoreRiskModal) — min=1 (default)
 * Drag updates local state only; the parent is notified once on pointer/key
 * release so a slow remote PUT does not fire per pixel of drag.
 */
// Full 0..5 palette (index === score). 0 = deepest red (worst).
const STOPS_0_5 = ['#dc2626', '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];
const TEXT_0_5 = ['text-red-700', 'text-red-600', 'text-orange-600', 'text-yellow-600', 'text-lime-600', 'text-green-600'];

// Exported 1..5 slices kept for backward-compatible external importers.
export const SCORE_STOPS = STOPS_0_5.slice(1); // 1..5
export const SCORE_TEXT_CLASS = TEXT_0_5.slice(1); // 1..5

interface ScoreSliderProps {
    label: string;
    value: number | null | undefined;
    disabled?: boolean;
    onCommit: (v: number) => void;
    /** Width in tailwind class form, default w-44 keeps it tight in toolbars. */
    widthClass?: string;
    /** When true, omit the tick row (e.g. when space is constrained). */
    hideTicks?: boolean;
    /** Lowest selectable score. 1 (default) keeps the legacy 1-5 scale; 0 enables the 0-5 control scale. */
    min?: number;
    /**
     * Reverse the colour scale (low=green … high=red). Use for "more is worse"
     * dimensions like inherent Likelihood/Severity. Default (false) keeps the
     * "more is better" scale used for control scores.
     */
    invert?: boolean;
}

const ScoreSlider: React.FC<ScoreSliderProps> = ({
    label,
    value,
    disabled = false,
    onCommit,
    widthClass = 'w-44',
    hideTicks = false,
    min = 1,
    invert = false,
}) => {
    const lo = min;
    const hi = 5;
    const span = hi - lo; // number of steps - 1
    const fallback = Math.max(lo, 3);
    const [local, setLocal] = useState<number>(value ?? fallback);
    useEffect(() => { setLocal(value ?? fallback); }, [value, fallback]);
    const isSet = value != null;
    const idx = Math.min(span, Math.max(0, local - lo));
    const colorIdx = invert ? span - idx : idx;
    const barColor = STOPS_0_5[lo + colorIdx];
    const valueClass = isSet ? TEXT_0_5[lo + colorIdx] : 'text-slate-400';
    const ticks = Array.from({ length: span + 1 }, (_, i) => lo + i);

    return (
        <div className={widthClass}>
            <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">{label}</label>
                <span className={`text-sm font-bold w-6 text-center ${valueClass}`}>
                    {isSet ? local : '—'}
                </span>
            </div>
            <input
                type="range"
                min={lo}
                max={hi}
                step={1}
                value={local}
                disabled={disabled}
                onChange={(e) => setLocal(parseInt(e.target.value, 10))}
                onMouseUp={(e) => onCommit(parseInt((e.target as HTMLInputElement).value, 10))}
                onTouchEnd={(e) => onCommit(parseInt((e.target as HTMLInputElement).value, 10))}
                onKeyUp={(e) => onCommit(parseInt((e.target as HTMLInputElement).value, 10))}
                className="w-full h-1 rounded-full appearance-none cursor-pointer disabled:opacity-50"
                style={{ background: barColor, accentColor: barColor }}
            />
            {!hideTicks && (
                <div className="flex justify-between text-[9px] text-slate-400 mt-0.5 px-0.5">
                    {ticks.map(t => <span key={t}>{t}</span>)}
                </div>
            )}
        </div>
    );
};

export default ScoreSlider;
